const OUTCOME_TONE = {
  "Churn risk": "is-risk",
  "Ready to move": "is-positive",
  Closing: "is-positive",
  Stalled: "is-warning",
  Cool: "is-neutral",
  "Low momentum": "is-neutral",
  "In progress": "is-progress",
  Recorded: "is-neutral",
  "Outside customer workflow": "is-neutral",
};

const WIDE_DESKTOP_LINE_TARGET = 58;
const COMPACT_DESKTOP_LINE_TARGET = 42;
const MOBILE_LINE_TARGET = 30;

function normalizeDisplayLabel(value) {
  const cleaned = String(value || "")
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";

  return cleaned.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase());
}

export function displayOutcomeLabel(value) {
  const normalized = normalizeDisplayLabel(value);
  return normalized === "Cool" ? "Low momentum" : normalized;
}

function splitBalancedLines(value, targetLength) {
  const text = normalizeDisplayLabel(value);
  if (!text) return [];
  if (text.length <= targetLength) return [text];

  const words = text.split(" ");
  const estimatedLines = Math.max(2, Math.ceil(text.length / targetLength));
  const lines = [];
  let wordIndex = 0;

  for (let lineIndex = 0; lineIndex < estimatedLines && wordIndex < words.length; lineIndex += 1) {
    const remainingLines = estimatedLines - lineIndex;
    const remainingText = words.slice(wordIndex).join(" ");
    const idealLength = Math.ceil(remainingText.length / remainingLines);
    let line = "";

    while (wordIndex < words.length) {
      const candidate = line ? `${line} ${words[wordIndex]}` : words[wordIndex];
      const wordsLeftAfterCandidate = words.length - (wordIndex + 1);
      const mustLeaveWords = remainingLines - 1;

      if (line && candidate.length > idealLength && wordsLeftAfterCandidate >= mustLeaveWords) {
        break;
      }

      line = candidate;
      wordIndex += 1;

      if (line.length >= idealLength && wordsLeftAfterCandidate >= mustLeaveWords) {
        break;
      }
    }

    if (line) lines.push(line);
  }

  if (wordIndex < words.length) {
    const remainder = words.slice(wordIndex).join(" ");
    if (lines.length) lines[lines.length - 1] = `${lines[lines.length - 1]} ${remainder}`;
    else lines.push(remainder);
  }

  return lines;
}

function LineSet({ label, target, variant }) {
  const lines = splitBalancedLines(label, target);

  return (
    <span
      className={`conversation-status-chip__text conversation-status-chip__text--${variant}`}
      aria-hidden="true"
    >
      {lines.map((line, index) => (
        <span className="conversation-status-chip__line" key={`${variant}-${line}-${index}`}>
          {line}
        </span>
      ))}
    </span>
  );
}

function BookmarkText({ label, lineTargets }) {
  const wideTarget = lineTargets?.wide ?? WIDE_DESKTOP_LINE_TARGET;
  const compactTarget = lineTargets?.compact ?? COMPACT_DESKTOP_LINE_TARGET;
  const mobileTarget = lineTargets?.mobile ?? MOBILE_LINE_TARGET;

  return (
    <>
      <LineSet label={label} target={wideTarget} variant="wide" />
      <LineSet label={label} target={compactTarget} variant="compact" />
      <LineSet label={label} target={mobileTarget} variant="mobile" />
    </>
  );
}

function StatusChip({ label, tone = "is-neutral", testId, kind = "generic", naturalBookmark = false, bookmarkLineTargets }) {
  const normalizedLabel = normalizeDisplayLabel(label);
  if (!normalizedLabel) return null;

  return (
    <span
      data-testid={testId}
      className={`relationship-chip ${tone} conversation-status-chip conversation-status-chip--${kind}`}
      title={normalizedLabel}
      aria-label={normalizedLabel}
    >
      {kind === "bookmark" ? (
        naturalBookmark ? (
          <span className="conversation-status-chip__text conversation-status-chip__text--natural">
            {normalizedLabel}
          </span>
        ) : (
          <BookmarkText label={normalizedLabel} lineTargets={bookmarkLineTargets} />
        )
      ) : (
        <span className="conversation-status-chip__text conversation-status-chip__text--plain">
          {normalizedLabel}
        </span>
      )}
    </span>
  );
}

export default function ConversationStatusChips({
  outcome,
  stage,
  className = "",
  outcomeTestId,
  stageTestId,
  naturalBookmark = false,
  bookmarkLineTargets,
}) {
  const normalizedOutcome = displayOutcomeLabel(outcome);
  const normalizedStage = normalizeDisplayLabel(stage);
  if (!normalizedOutcome && !normalizedStage) return null;

  const outcomeTone = OUTCOME_TONE[normalizedOutcome] || OUTCOME_TONE.Recorded;

  return (
    <span className={`conversation-status-chips${naturalBookmark ? " has-natural-bookmark" : ""}${className ? ` ${className}` : ""}`}>
      {normalizedOutcome && (
        <StatusChip label={normalizedOutcome} tone={outcomeTone} testId={outcomeTestId} kind="outcome" />
      )}
      {normalizedStage && (
        <StatusChip
          label={normalizedStage}
          tone="is-stage"
          testId={stageTestId}
          kind="bookmark"
          naturalBookmark={naturalBookmark}
          bookmarkLineTargets={bookmarkLineTargets}
        />
      )}
    </span>
  );
}
